import React, { useEffect, useState, useRef } from "react";
import {
  Grid,
  MenuItem,
  TextField,
  Typography,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import FormDialog from "../../../components/FormDialog";
import {
  Log,
  getExchangeRateDetails,
  updateExchangeRate,
  createExchangeRate,
  getLastMonthRate,
} from "../../../services/services";
import moment from "moment";
import CustomDatePicker from "../../../components/form/CustomDatePicker";
import { EXCHANGE_RATE_CURRENCIES } from "../../../utils/constants";

const EditDialog = ({ open, onClose, selectedId, onSuccess }) => {
  const [loaded, setLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingPreviousMonth, setIsLoadingPreviousMonth] = useState(false);
  const isSubmittingRef = useRef(false);
  const isEdit = !!selectedId;

  const {
    handleChange,
    handleSubmit,
    handleBlur,
    values,
    touched,
    errors,
    setValues,
    resetForm,
  } = useFormik({
    initialValues: {
      to_currency: "",
      rate_date: moment().format("YYYY-MM"),
      rate: "",
    },
    validationSchema: yup.object().shape({
      to_currency: yup
        .string()
        .required("Required")
        .max(50, "Maximum 50 characters"),
      rate_date: yup
        .string()
        .required("Required")
        .test("is-valid-month", "Invalid month format (YYYY-MM)", (value) => {
          if (!value) return false;
          return moment(value, "YYYY-MM", true).isValid();
        }),
      rate: yup
        .number()
        .required("Required")
        .min(0, "Rate must be greater than or equal to 0")
        .test(
          "less-than-10",
          "Rate must be less than 10",
          value => value !== undefined && value < 10
        ),
    }),
    onSubmit: async (values) => {
      if (isSubmittingRef.current) {
        return;
      }
      
      isSubmittingRef.current = true;
      setIsSubmitting(true);
      
      try {
        const submitValues = {
          to_currency: values.to_currency,
          rate_date: values.rate_date,
          rate: values.rate,
        };

        if (isEdit) {
          Log.info(
            `Click button [Submit] - Update Exchange Rate by Id: ${selectedId}`,
            submitValues
          );
          await updateExchangeRate(selectedId, submitValues, onSuccess);
        } else {
          Log.info(
            "Click button [Submit] - Create Exchange Rate",
            submitValues
          );
          await createExchangeRate(submitValues, onSuccess);
        }
      } catch (error) {
        console.error("Error submitting form:", error);
      } finally {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (open) {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      
      if (isEdit && selectedId) {
        setLoaded(false);
        getExchangeRateDetails(
          selectedId,
          (data) => {
            setValues({
              to_currency: data.to_currency || "",
              rate_date: data.rate_date
                ? moment(data.rate_date).format("YYYY-MM")
                : "",
              rate: data.rate !== null && data.rate !== undefined
                ? parseFloat(data.rate).toFixed(7)
                : "",
            });
            setLoaded(true);
          },
          setLoaded
        );
      } else {
        resetForm();
        setLoaded(true);
      }
    } else {
      setLoaded(false);
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [open, selectedId, isEdit, setValues, resetForm]);

  useEffect(() => {
    let isMounted = true;
    
    if (open && !isEdit && values.to_currency && values.rate_date) {
      const selectedMonth = moment(values.rate_date, "YYYY-MM");
      const previousMonth = selectedMonth.clone().subtract(1, 'month');
      const previousMonthLastDay = previousMonth.endOf('month').format("YYYY-MM-DD");
      
      setIsLoadingPreviousMonth(true);
      getLastMonthRate(
        values.to_currency,
        previousMonthLastDay,
        (data) => {
          if (isMounted && data && data.rate !== null) {
            setValues((prevValues) => ({
              ...prevValues,
              rate: data.rate !== null && data.rate !== undefined
                ? parseFloat(data.rate).toFixed(7)
                : prevValues.rate,
            }));
          }
          if (isMounted) {
            setIsLoadingPreviousMonth(false);
          }
        },
        () => {
          if (isMounted) {
            setIsLoadingPreviousMonth(false);
          }
        }
      );
    } else {
      setIsLoadingPreviousMonth(false);
    }
    
    return () => {
      isMounted = false;
    };
  }, [values.rate_date, values.to_currency, open, isEdit, setValues]);

  const handleClose = () => {
    resetForm();
    isSubmittingRef.current = false;
    setIsSubmitting(false);
    onClose();
  };

  const handleFormSubmit = React.useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (isSubmittingRef.current || isSubmitting) {
      return false;
    }
    
    handleSubmit(e);
    
    return false;
  }, [isSubmitting, isLoadingPreviousMonth, handleSubmit]);

  if (!open) return null;

  const isRateDisabled =
    isSubmitting || (!isEdit && (!values.to_currency || !values.rate_date || isLoadingPreviousMonth));
  const ratePlaceholder =
    isLoadingPreviousMonth
      ? "Loading previous month rate..."
      : !isEdit && (!values.to_currency || !values.rate_date)
      ? "Select To Currency and Rate Date first"
      : "e.g., 0.8304000";

  return loaded ? (
    <FormDialog
      open={open}
      onClose={handleClose}
      title={isEdit ? "Edit Exchange Rate" : "Add New Exchange Rate"}
      handleSubmit={handleFormSubmit}
      loading={isSubmitting}
      disabledSubmit={isLoadingPreviousMonth || isSubmitting}
      maxWidth="md"
    >
      <form onSubmit={handleFormSubmit} noValidate>
        <Grid container spacing={2} alignItems="center">
        <Grid item md={4} xs={12}>
          <Typography variant="subtitle1" align="right">To Currency *</Typography>
        </Grid>
        <Grid item md={8} xs={12}>
          <TextField
            name="to_currency"
            select
            value={values.to_currency || ""}
            onChange={handleChange}
            onBlur={handleBlur}
            variant="outlined"
            fullWidth
            size="small"
            margin="dense"
            error={Boolean(touched.to_currency && errors.to_currency)}
            helperText={touched.to_currency && errors.to_currency}
            disabled={isSubmitting}
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected) => {
                if (!selected) {
                  return <span style={{ color: '#999' }}>Select</span>;
                }
                return selected;
              }
            }}
          >
            {EXCHANGE_RATE_CURRENCIES.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item md={4} xs={12}>
          <Typography variant="subtitle1" align="right">Rate Date (Month/Year) *</Typography>
        </Grid>
        <Grid item md={8} xs={12}>
          <CustomDatePicker
            inputFormat="YYYY-MM"
            views={['year', 'month']}
            openTo="month"
            value={
              values.rate_date
                ? moment(values.rate_date, "YYYY-MM").isValid()
                  ? moment(values.rate_date, "YYYY-MM")
                  : null
                : null
            }
            onChange={(newValue) => {
              const formattedDate = newValue
                ? moment(newValue).format("YYYY-MM")
                : "";
              handleChange({
                target: { name: "rate_date", value: formattedDate },
              });
            }}
            error={Boolean(touched.rate_date && errors.rate_date)}
            helperText={touched.rate_date && errors.rate_date}
            disabled={isSubmitting}
            margin="dense"
          />
        </Grid>

        <Grid item md={4} xs={12}>
          <Typography variant="subtitle1" align="right">Rate *</Typography>
        </Grid>
        <Grid item md={8} xs={12}>
          <TextField
            name="rate"
            type="number"
            value={values.rate}
            onChange={handleChange}
            onBlur={handleBlur}
            variant="outlined"
            fullWidth
            size="small"
            margin="dense"
            placeholder={ratePlaceholder}
            inputProps={{ step: "0.0000001", min: "0" }}
            error={Boolean(touched.rate && errors.rate)}
            helperText={touched.rate && errors.rate}
            disabled={isRateDisabled}
          />
        </Grid>
      </Grid>
      </form>
    </FormDialog>
  ) : (
    <Backdrop
      sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      open={open}
    >
      <CircularProgress color="inherit" />
    </Backdrop>
  );
};

export default EditDialog;
